import { CAPTAINS, getModeLabel } from './content';
import type { CaptainId, GameMode, ResourceWallet, RunModifiers, RunResult, StageDefinition } from './types';

export type TargetKind = 'walker' | 'runner' | 'blocker' | 'spitter' | 'barrier' | 'crate' | 'boss';
export type GateEffect = 'add' | 'multiply' | 'subtract' | 'damage' | 'rate' | 'support';

export interface TargetSnapshot {
  id: number;
  kind: TargetKind;
  x: number;
  z: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  bossName?: string;
}

export interface GateOption {
  effect: GateEffect;
  value: number;
  label: string;
  positive: boolean;
}

export interface GateSnapshot {
  id: number;
  z: number;
  resolved: boolean;
  selectedSide: 'left' | 'right' | null;
  left: GateOption;
  right: GateOption;
}

export interface SimulationSnapshot {
  status: 'running' | 'won' | 'lost' | 'paused';
  mode: GameMode;
  modeLabel: string;
  stageId: number;
  stageName: string;
  accent: string;
  biome: StageDefinition['biome'];
  elapsed: number;
  distance: number;
  courseLength: number;
  progress: number;
  playerX: number;
  targetX: number;
  troops: number;
  maxTroops: number;
  damagePerUnit: number;
  fireRate: number;
  range: number;
  supportDrones: number;
  kills: number;
  gatesPassed: number;
  shieldReady: boolean;
  focusMultiplier: number;
  currentTargetId: number | null;
  targets: TargetSnapshot[];
  gates: GateSnapshot[];
}

export type SimulationEvent =
  | { type: 'shot'; fromX: number; targetId: number; targetX: number; targetZ: number; critical: boolean }
  | { type: 'hit'; targetId: number; amount: number }
  | { type: 'destroy'; targetId: number; kind: TargetKind; x: number; z: number }
  | { type: 'hurt'; amount: number; shielded: boolean }
  | { type: 'gate'; option: GateOption; side: 'left' | 'right' }
  | { type: 'support'; drones: number }
  | { type: 'boss'; active: boolean }
  | { type: 'finish'; result: RunResult };

interface MutableTarget extends TargetSnapshot {
  damage: number;
  speed: number;
  phase: number;
  attackCooldown: number;
}

interface MutableGate extends GateSnapshot {}

export interface RunSimulationOptions {
  stage: StageDefinition;
  mode: GameMode;
  captainId: CaptainId;
  modifiers: RunModifiers;
  seed?: number;
}

function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function moveTowards(current: number, target: number, maxDelta: number): number {
  if (Math.abs(target - current) <= maxDelta) return target;
  return current + Math.sign(target - current) * maxDelta;
}

function modeScale(mode: GameMode): number {
  if (mode === 'hard') return 1.38;
  if (mode === 'daily') return 1.12;
  return 1;
}

const BOSS_ENGAGE_DISTANCE = 52;

export class RunSimulation {
  private readonly stage: StageDefinition;
  private readonly mode: GameMode;
  private readonly captainId: CaptainId;
  private readonly random: () => number;
  private readonly events: SimulationEvent[] = [];
  private readonly targets: MutableTarget[] = [];
  private readonly gates: MutableGate[] = [];
  private status: SimulationSnapshot['status'] = 'running';
  private previousStatus: SimulationSnapshot['status'] = 'running';
  private elapsed = 0;
  private distance = 0;
  private playerX = 0;
  private targetX = 0;
  private troops: number;
  private maxTroops: number;
  private damagePerUnit: number;
  private fireRate: number;
  private readonly range: number;
  private supportDrones = 0;
  private kills = 0;
  private gatesPassed = 0;
  private shieldReady: boolean;
  private shotCooldown = 0;
  private currentTargetId: number | null = null;
  private focusTime = 0;
  private bossActivated = false;
  private result: RunResult | null = null;

  constructor(options: RunSimulationOptions) {
    this.stage = options.stage;
    this.mode = options.mode;
    this.captainId = options.captainId;
    this.random = mulberry32(options.seed ?? options.stage.seed + (options.mode === 'daily' ? this.dailySeed() : 0));
    this.troops = options.modifiers.startTroops;
    this.maxTroops = this.troops;
    this.damagePerUnit = options.modifiers.damagePerUnit;
    this.fireRate = options.modifiers.fireRate;
    this.range = options.modifiers.range;
    this.shieldReady = options.captainId === 'vanguard';
    this.generateEncounter(options.modifiers.supportBonus);
  }

  private dailySeed(): number {
    const now = new Date();
    return Number([
      now.getUTCFullYear(),
      String(now.getUTCMonth() + 1).padStart(2, '0'),
      String(now.getUTCDate()).padStart(2, '0'),
    ].join(''));
  }

  private generateEncounter(supportBonus: number): void {
    const scale = modeScale(this.mode);
    const laneValues = [-0.72, 0, 0.72];
    const gateFractions = [0.1, 0.18, 0.26, 0.34, 0.42, 0.5, 0.58, 0.66, 0.74, 0.82];
    const gatePositions = gateFractions.map((fraction) => Math.round(this.stage.length * fraction));
    let id = 1;

    for (let z = 18; z < this.stage.length - 28; z += 12.5) {
      const gateZone = gatePositions.some((gateZ) => Math.abs(gateZ - z) < 6.5);
      if (gateZone) continue;
      const roll = this.random();
      const lane = laneValues[Math.floor(this.random() * laneValues.length)];
      const kind: TargetKind =
        roll < 0.46 ? 'walker' : roll < 0.65 ? 'runner' : roll < 0.79 ? 'blocker' : roll < 0.9 ? 'spitter' : roll < 0.96 ? 'barrier' : 'crate';
      const hpMultiplier =
        kind === 'walker'
          ? 1.15
          : kind === 'runner'
            ? 0.95
            : kind === 'blocker'
              ? 2.5
              : kind === 'barrier'
                ? 3.2
                : kind === 'crate'
                  ? 1.15
                  : 1.45;
      const progress = z / this.stage.length;
      const progressScale = 1 + 16 * Math.pow(progress, 1.55);
      const maxHp = Math.ceil(this.stage.enemyHp * hpMultiplier * progressScale * scale);
      this.targets.push({
        id: id++,
        kind,
        x: lane,
        z: z + this.random() * 3,
        hp: maxHp,
        maxHp,
        damage: kind === 'barrier' ? this.stage.enemyDamage + 4 : kind === 'blocker' ? this.stage.enemyDamage + 2 : this.stage.enemyDamage,
        speed: kind === 'runner' ? 0.42 + this.stage.id * 0.008 : 0,
        phase: this.random() * Math.PI * 2,
        alive: true,
        attackCooldown: 1.5 + this.random(),
      });
    }

    const gatePatterns: Array<[GateOption, GateOption]> = [
      [
        { effect: 'add', value: 8 + this.stage.chapter * 2, label: '+' + (8 + this.stage.chapter * 2), positive: true },
        { effect: 'damage', value: 25, label: 'DMG +25%', positive: true },
      ],
      [
        { effect: 'multiply', value: 1.55, label: '×1.55', positive: true },
        { effect: 'rate', value: 24, label: 'RATE +24%', positive: true },
      ],
      [
        { effect: 'subtract', value: 6 + this.stage.chapter * 2, label: '−' + (6 + this.stage.chapter * 2), positive: false },
        { effect: 'add', value: 15 + this.stage.order, label: '+' + (15 + this.stage.order), positive: true },
      ],
      [
        { effect: 'support', value: supportBonus, label: 'DRONE', positive: true },
        { effect: 'damage', value: 32, label: 'DMG +32%', positive: true },
      ],
    ];

    gatePositions.forEach((z, gateIndex) => {
      if (z >= this.stage.length - 13) return;
      const pattern = gatePatterns[(gateIndex + this.stage.id) % gatePatterns.length];
      const flip = this.random() > 0.5;
      this.gates.push({
        id: 1000 + gateIndex,
        z,
        resolved: false,
        selectedSide: null,
        left: flip ? pattern[1] : pattern[0],
        right: flip ? pattern[0] : pattern[1],
      });
    });

    const bossHp = this.stage.bossHp * scale;
    this.targets.push({
      id: 9000,
      kind: 'boss',
      x: 0,
      z: this.stage.length,
      hp: bossHp,
      maxHp: bossHp,
      damage: Math.ceil(this.stage.bossDamage * scale),
      speed: 0,
      phase: 0,
      alive: true,
      bossName: this.stage.bossName,
      attackCooldown: 1.25,
    });
  }

  setTargetX(value: number): void {
    this.targetX = clamp(value, -1, 1);
  }

  setPaused(paused: boolean): void {
    if (this.status === 'won' || this.status === 'lost') return;
    if (paused && this.status === 'running') {
      this.previousStatus = this.status;
      this.status = 'paused';
    } else if (!paused && this.status === 'paused') {
      this.status = this.previousStatus;
    }
  }

  update(deltaSeconds: number): void {
    if (this.status !== 'running') return;
    const dt = clamp(deltaSeconds, 0, 0.05);
    this.elapsed += dt;
    this.playerX = moveTowards(this.playerX, this.targetX, 3.4 * dt);

    const boss = this.targets.find((target) => target.kind === 'boss');
    const bossDistance = boss ? boss.z - this.distance : Number.POSITIVE_INFINITY;
    if (!this.bossActivated && bossDistance <= BOSS_ENGAGE_DISTANCE) {
      this.bossActivated = true;
      this.events.push({ type: 'boss', active: true });
    }
    if (!this.bossActivated) {
      this.distance = Math.min(this.stage.length - this.range * 0.68, this.distance + this.stage.speed * dt);
    }

    this.updateGates();
    this.updateTargets(dt);
    this.updateShooting(dt);
    if (this.troops <= 0) this.finish(false, 'troops-lost');
  }

  private updateGates(): void {
    for (const gate of this.gates) {
      if (gate.resolved || gate.z > this.distance + 0.45) continue;
      gate.resolved = true;
      const side = this.chooseGateSide();
      gate.selectedSide = side;
      const option = gate[side];
      this.applyGate(option);
      this.gatesPassed += 1;
      this.events.push({ type: 'gate', option, side });
    }
  }

  private chooseGateSide(): 'left' | 'right' {
    return this.playerX < 0 ? 'left' : 'right';
  }

  private applyGate(option: GateOption): void {
    if (option.effect === 'add') this.troops += Math.round(option.value);
    if (option.effect === 'multiply') this.troops = Math.floor(this.troops * option.value);
    if (option.effect === 'subtract') this.takeDamage(Math.round(option.value));
    if (option.effect === 'damage') this.damagePerUnit *= 1 + option.value / 100;
    if (option.effect === 'rate') this.fireRate *= 1 + option.value / 100;
    if (option.effect === 'support') {
      const added = this.captainId === 'technician' ? 2 : 1;
      this.supportDrones += added;
      this.events.push({ type: 'support', drones: added });
    }
    this.troops = clamp(Math.round(this.troops), 0, 180);
    this.maxTroops = Math.max(this.maxTroops, this.troops);
  }

  private updateTargets(dt: number): void {
    for (const target of this.targets) {
      if (!target.alive) continue;
      const relative = target.z - this.distance;

      if (target.kind === 'runner') {
        target.phase += dt * 2.2;
        target.x = clamp(target.x + Math.sin(target.phase) * dt * 0.28, -0.84, 0.84);
        target.z -= target.speed * dt;
      }
      if (target.kind === 'spitter' && relative < 17 && relative > 1.2) {
        target.attackCooldown -= dt;
        if (target.attackCooldown <= 0) {
          if (Math.abs(target.x - this.playerX) < 0.46) this.takeDamage(Math.max(1, Math.floor(target.damage / 2)));
          target.attackCooldown = 2.1;
        }
      }
      if (target.kind === 'boss' && this.bossActivated) {
        target.attackCooldown -= dt;
        if (target.attackCooldown <= 0) {
          this.takeDamage(target.damage);
          target.attackCooldown = Math.max(0.78, 1.45 - this.stage.id * 0.018);
        }
        continue;
      }
      if (relative <= 0.55) {
        const laneHit = Math.abs(target.x - this.playerX) < (target.kind === 'barrier' ? 0.64 : 0.42);
        if (laneHit) this.takeDamage(target.damage);
        target.alive = false;
      }
    }
  }

  private updateShooting(dt: number): void {
    this.shotCooldown -= dt;
    const target = this.findTarget();
    if (!target) {
      this.currentTargetId = null;
      this.focusTime = 0;
      // 空白区間で負の待ち時間を蓄積すると、次の敵へ毎フレーム連射してしまう。
      // 標的がいない間は発射可能状態に留め、過去の射撃回数を持ち越さない。
      this.shotCooldown = 0;
      return;
    }
    if (this.currentTargetId === target.id) this.focusTime += dt;
    else {
      this.currentTargetId = target.id;
      this.focusTime = 0;
    }
    if (this.shotCooldown > 0) return;
    this.shotCooldown = 1 / this.fireRate;
    const focusMultiplier = this.captainId === 'gunner' ? 1 + Math.min(0.5, this.focusTime * 0.08) : 1;
    const droneMultiplier = 1 + this.supportDrones * 0.18;
    const damage = this.troops * this.damagePerUnit * 0.29 * focusMultiplier * droneMultiplier;
    target.hp -= damage;
    const critical = this.random() < 0.08;
    if (critical) target.hp -= damage * 0.5;
    this.events.push({ type: 'shot', fromX: this.playerX, targetId: target.id, targetX: target.x, targetZ: target.z, critical });
    this.events.push({ type: 'hit', targetId: target.id, amount: damage });
    if (target.hp <= 0) this.destroyTarget(target);
  }

  private findTarget(): MutableTarget | undefined {
    return this.targets
      .filter((target) => {
        if (!target.alive) return false;
        if (target.kind === 'boss' && !this.bossActivated) return false;
        const relative = target.z - this.distance;
        const targetRange = target.kind === 'boss' && this.bossActivated ? Math.max(this.range, BOSS_ENGAGE_DISTANCE) : this.range;
        if (relative < 0 || relative > targetRange) return false;
        const tolerance = target.kind === 'boss' ? 1.15 : target.kind === 'barrier' ? 0.55 : 0.48;
        return Math.abs(target.x - this.playerX) <= tolerance;
      })
      .sort((a, b) => a.z - b.z)[0];
  }

  private destroyTarget(target: MutableTarget): void {
    target.alive = false;
    this.events.push({ type: 'destroy', targetId: target.id, kind: target.kind, x: target.x, z: target.z });
    if (target.kind === 'boss') {
      this.kills += 1;
      this.finish(true, 'victory');
      return;
    }
    if (target.kind === 'crate') {
      this.troops = clamp(this.troops + 5, 0, 180);
      this.maxTroops = Math.max(this.maxTroops, this.troops);
    } else if (target.kind !== 'barrier') {
      this.kills += 1;
    }
  }

  private takeDamage(amount: number): void {
    const normalized = Math.max(1, Math.ceil(amount));
    if (normalized >= this.troops && this.shieldReady) {
      this.shieldReady = false;
      this.troops = 5;
      this.events.push({ type: 'hurt', amount: normalized, shielded: true });
      return;
    }
    this.troops = Math.max(0, this.troops - normalized);
    this.events.push({ type: 'hurt', amount: normalized, shielded: false });
  }

  private finish(victory: boolean, reason: RunResult['reason']): void {
    if (this.result) return;
    this.status = victory ? 'won' : 'lost';
    const progress = victory ? 1 : clamp(this.distance / this.stage.length, 0, 1);
    const stars = victory ? 1 + Number(this.troops >= this.maxTroops * 0.45) + Number(this.troops >= this.maxTroops * 0.72) : 0;
    const rewardScale = this.mode === 'hard' ? 1.5 : this.mode === 'daily' ? 1.25 : 1;
    const completionScale = victory ? 1 : Math.max(0.25, progress * 0.55);
    const rewards: ResourceWallet = {
      food: Math.round((34 + this.stage.id * 6 + this.troops * 0.5) * rewardScale * completionScale),
      metal: Math.round((28 + this.stage.id * 5 + this.kills * 1.5) * rewardScale * completionScale),
      electronics: Math.round((4 + this.stage.chapter * 3 + this.gatesPassed) * rewardScale * completionScale),
      blueprints: victory && this.stage.order === 5 && this.mode === 'normal' ? 1 : 0,
    };
    this.result = {
      victory,
      stageId: this.stage.id,
      mode: this.mode,
      stars,
      troopsLeft: this.troops,
      maxTroops: this.maxTroops,
      kills: this.kills,
      gates: this.gatesPassed,
      progress,
      reason,
      rewards,
    };
    this.events.push({ type: 'finish', result: this.result });
  }

  quit(): RunResult {
    this.finish(false, 'quit');
    return this.result as RunResult;
  }

  getResult(): RunResult | null {
    return this.result;
  }

  drainEvents(): SimulationEvent[] {
    return this.events.splice(0, this.events.length);
  }

  getEncounterDigest(): string {
    return this.targets
      .map((target) => [target.kind, target.x.toFixed(2), target.z.toFixed(2), target.maxHp.toFixed(1)].join(':'))
      .join('|');
  }

  getSnapshot(): SimulationSnapshot {
    const focusMultiplier = this.captainId === 'gunner' ? 1 + Math.min(0.5, this.focusTime * 0.08) : 1;
    return {
      status: this.status,
      mode: this.mode,
      modeLabel: getModeLabel(this.mode),
      stageId: this.stage.id,
      stageName: this.stage.name,
      accent: this.stage.accent,
      biome: this.stage.biome,
      elapsed: this.elapsed,
      distance: this.distance,
      courseLength: this.stage.length,
      progress: clamp(this.distance / this.stage.length, 0, 1),
      playerX: this.playerX,
      targetX: this.targetX,
      troops: this.troops,
      maxTroops: this.maxTroops,
      damagePerUnit: this.damagePerUnit,
      fireRate: this.fireRate,
      range: this.range,
      supportDrones: this.supportDrones,
      kills: this.kills,
      gatesPassed: this.gatesPassed,
      shieldReady: this.shieldReady,
      focusMultiplier,
      currentTargetId: this.currentTargetId,
      targets: this.targets.map((target) => ({ ...target })),
      gates: this.gates.map((gate) => ({ ...gate, left: { ...gate.left }, right: { ...gate.right } })),
    };
  }

  getCaptainName(): string {
    return CAPTAINS[this.captainId].name;
  }
}
